import {
  put,
  takeLatest,
  takeEvery,
  call,
  delay,
  select
} from 'redux-saga/effects';
import { serializeKeys, deserializeKeys } from '@iso/lib/helpers/keyNormalizer';
import request from '@iso/lib/helpers/httpClient';
import * as CONSTANTS from './constants';
import * as ACTIONS from './actions';
import * as USER_ACTIONS from '../user/actions';
import * as WIZARD_ACTIONS from '../accountWizard/actions';
import notify from '@iso/lib/helpers/notify';
import { ACCOUNT_TYPES, LOCAL_STORAGE_LABELS } from '@iso/enums';

function* fetchCompanies() {
  try {
    const data = yield call(request, '/company/', 'GET', null, false);
    yield put(ACTIONS.fetchCompaniesSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchCompaniesFail(error));
  }
}

function* fetchCompanyDetail({ id }) {
  try {
    const data = yield call(request, `/company/${id}/`, 'GET', null, true);
    yield put(ACTIONS.fetchCompanyDetailSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchCompanyDetailFail(error));
  }
}

function* fetchCompanyProfile({ id }) {
  try {
    const data = yield call(
      request,
      `/production_company/${id}/profile/`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.fetchCompanyProfileSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchCompanyProfileFail(error));
  }
}

// TODO: remove identical endpoint from client/src/redux/accountWizard/saga.js
function* createCompany({ payload }) {
  try {
    const data = yield call(
      request,
      `/company/`,
      'POST',
      deserializeKeys(payload),
      true
    );
    yield put(ACTIONS.createCompanySuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.createCompanyFail(error));
  }
}

function* updateCompany({
  id,
  payload,
  callBack = () => {},
  onError = () => {}
}) {
  try {
    const data = yield call(
      request,
      `/company/${id}/`,
      'PATCH',
      deserializeKeys(payload),
      true
    );
    callBack();
    yield put(ACTIONS.updateCompanySuccess(serializeKeys(data)));
  } catch (error) {
    onError(error);
    yield put(ACTIONS.updateCompanyFail(error));
  }
}

function* updateCompanyProfile({ id, payload }) {
  try {
    const data = yield call(
      request,
      `/production_company/${id}/profile/`,
      'POST',
      deserializeKeys(payload),
      true
    );
    yield put(ACTIONS.updateCompanyProfileSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.updateCompanyProfileFail(error));
  }
}

function* acceptCompanyPermissionByOwner({ token }) {
  try {
    const returnUri = yield call(
      request,
      `/validation/accept_company_access_token/${token}/`,
      'GET',
      null,
      false
    );
    yield put(ACTIONS.acceptCompanyPermissionByOwnerSuccess());
    window.location.href = returnUri;
  } catch (error) {
    yield put(ACTIONS.acceptCompanyPermissionByOwnerFail(error));
  }
}

function* declineCompanyPermissionByOwner({ token }) {
  try {
    const returnUri = yield call(
      request,
      `/validation/decline_company_access_token/${token}/`,
      'GET',
      null,
      false
    );
    yield put(ACTIONS.declineCompanyPermissionByOwnerSuccess());
    window.location.href = returnUri;
  } catch (error) {
    yield put(ACTIONS.declineCompanyPermissionByOwnerFail(error));
  }
}

function* fetchCompanyType() {
  try {
    const data = yield call(request, `/company_types/`, 'GET', null, true);
    yield put(ACTIONS.fetchCompanyTypeSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchCompanyTypeFail(error));
  }
}

function* fetchBusinessType() {
  try {
    const data = yield call(request, `/business_types/`, 'GET', null, true);
    yield put(ACTIONS.fetchBusinessTypeSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchBusinessTypeFail(error));
  }
}

function* registerSilaCompany(companyInfo) {
  try {
    const { id, payload } = companyInfo.payload;
    const data = yield call(
      request,
      `/company/${id}/sila/register/`,
      'POST',
      deserializeKeys(payload),
      true
    );
    yield put(ACTIONS.registerSilaCompanySuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.registerSilaCompanyFail(error));
  }
}

function* updateSilaCompany(companyInfo) {
  try {
    const { id, payload } = companyInfo.payload;
    const data = yield call(
      request,
      `/company/${id}/sila/corporate-info/`,
      'PUT',
      deserializeKeys(payload),
      true
    );
    yield put(ACTIONS.updateSilaCompanySuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.updateSilaCompanyFail(error));
  }
}

function* getCompanyWallet({ payload }) {
  try {
    const id = payload;
    const data = yield call(
      request,
      `/company/${id}/sila/wallet/`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.getCompanyWalletSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.getCompanyWalletFail(error));
  }
}

function* getCorporateSilaCompany({ payload }) {
  try {
    const id = payload;
    const data = yield call(
      request,
      `/company/${id}/sila/corporate-info/`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.getCorporateSilaCompanySuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.getCorporateSilaCompanyFail(error));
  }
}

function* getCompanySilaKYB({ payload: id }) {
  try {
    const data = yield call(
      request,
      `/company/${id}/sila/kyb/`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.getCompanySilaKYBSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.getCompanySilaKYBFail(error.error));
  }
}

function* getCompanySilaKYBInterval(action) {
  yield delay(10000);
  yield call(getCompanySilaKYB, action);
}

function* postCompanyRequestKYB({ payload }) {
  try {
    const id = payload;
    const data = yield call(
      request,
      `/company/${id}/sila/kyb/`,
      'POST',
      null,
      true
    );
    yield put(ACTIONS.postCompanyRequestKYBSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.postCompanyRequestKYBFail(error));
  }
}

function* uploadKYBDocumentRequest({
  payload,
  callBack = () => {},
  onError = () => {}
}) {
  try {
    const { companyId, formData } = payload;
    const data = yield call(
      request,
      `/company/${companyId}/sila/kyb/document`,
      'POST',
      formData,
      true,
      false,
      true
    );
    callBack();
    yield put(ACTIONS.uploadKYBDocumentSuccess(serializeKeys(data)));
  } catch (error) {
    onError(error);
    yield put(ACTIONS.uploadKYBDocumentFail(error));
  }
}

function* getPlaidLinkCompanyToken({ payload }) {
  try {
    const {
      id,
      plaidToken,
      accounts,
      accessToken,
      accountMetadata,
      oldAccessToken
    } = payload;
    if (plaidToken) {
      let param = {
        public_token: plaidToken,
        company_id: id,
        accounts
      };

      if (accountMetadata) param.account_metadata = accountMetadata;
      if (oldAccessToken) param.old_access_token = oldAccessToken;

      yield call(
        request,
        `/user/plaid/token/`,
        'PATCH',
        deserializeKeys(param),
        true
      );
      yield put(ACTIONS.getPlaidLinkCompanyAccountConnected());
    } else {
      let param = {
        company_id: id,
        redirect_uri: '/plaid/oauth/continue'
      };
      if (accessToken) param.accessToken = accessToken;
      const data = yield call(
        request,
        `/user/plaid/token/`,
        'POST',
        deserializeKeys(param),
        true
      );
      yield put(ACTIONS.getPlaidLinkCompanyTokenSuccess(serializeKeys(data)));
    }
  } catch (error) {
    yield put(ACTIONS.getPlaidLinkCompanyTokenFail(error));
  }
}

function* getPlaidCompanyAccount({ payload }) {
  try {
    const id = payload;
    const data = yield call(
      request,
      `/company/${id}/plaid/account/`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.getPlaidCompanyAccountSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.getPlaidCompanyAccountFail(error));
  }
}

function* getCompanyPlaidVerifyAccounts({ payload }) {
  try {
    const id = payload;
    const data = yield call(
      request,
      `/company/${id}/plaid/verify_account/`,
      'GET',
      null,
      true
    );
    yield put(
      ACTIONS.getCompanyPlaidVerifyAccountsSuccess(serializeKeys(data?.results))
    );
  } catch (error) {
    yield put(ACTIONS.getCompanyPlaidVerifyAccountsFail(error));
  }
}

function* removePlaidCompanyAccount({ payload }) {
  try {
    const companyId = payload.company_id;
    const accountId = payload.account_id;
    const data = yield call(
      request,
      `/company/${companyId}/plaid/account/${accountId}`,
      'DELETE',
      null,
      true
    );
    yield put(ACTIONS.removePlaidCompanyAccountSuccess(serializeKeys(data)));
  } catch (error) {
    let dataError = {
      message: 'Can not remove company bank account'
    };
    yield put(ACTIONS.removePlaidCompanyAccountFail(dataError));
  }
}

// bank account = plaid account
function* fetchCompanyWalletBal({ companyID }) {
  try {
    const data = yield call(
      request,
      `/company/${companyID}/check_balance`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.fetchCompanyWalletBalSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchCompanyWalletBalFail(error));
  }
}

function* deleteCompanyAccount({ companyID, payload }) {
  try {
    const data = yield call(
      request,
      `/company/${companyID}/delete`,
      'POST',
      payload,
      true
    );
    if (data) {
      if (data?.status === 'ok') {
        yield put(ACTIONS.deleteCompanyAccountSuccess(data));
      } else if (data?.status === 'Incorrect password') {
        yield put(ACTIONS.deleteCompanyAccountFail(data));
        notify(
          'error',
          'You have entered incorrect password, please try again!'
        );
      } else if (data?.status === 'User does not have access to company') {
        yield put(ACTIONS.deleteCompanyAccountFail(data));
        notify('error', 'Permission denied!');
      } else if (data?.status === 'The company contains unfinished jobs') {
        yield put(ACTIONS.deleteCompanyAccountFail(data));
        notify(
          'error',
          'You have active jobs. Please finish or archive them and try again!'
        );
      }
    }
  } catch (error) {
    yield put(ACTIONS.deleteCompanyAccountFail(error));
  }
}

function* setCompanyPaymentPlan({ payload }) {
  try {
    const data = yield call(
      request,
      '/stripe/subscription/',
      'POST',
      payload,
      true
    );
    yield put(ACTIONS.setCompanyPaymentPlanSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.setCompanyPaymentPlanFail(error));
  }
}

function* registerCompany({ payload }) {
  // TODO: replace this chain of API calls with one request: all necessary request will be implemented on backend side
  localStorage.removeItem(LOCAL_STORAGE_LABELS.NEW_COMPANY_ID);
  const { companyData, silaData, KYBCallBack } = payload;
  const user = yield select((state) => state.Auth.user);

  yield put(ACTIONS.clearCompany());
  yield call(createCompany, { payload: companyData });
  yield put(
    USER_ACTIONS.updateUserRequest(user.id, { type: ACCOUNT_TYPES.COMPANY })
  );
  const company = yield select(({ Company }) => Company.company);
  if (company.error) {
    WIZARD_ACTIONS.setWalletAPIErrors(company.error);
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_LABELS.NEW_COMPANY_ID, company.id);
  yield call(registerSilaCompany, {
    payload: { id: company.id, payload: silaData }
  });
  const silaCompany = yield select(({ Company }) => Company.silaCompany);
  if (silaCompany.error) {
    WIZARD_ACTIONS.setWalletAPIErrors(silaCompany.error);
    return;
  }
  yield call(postCompanyRequestKYB, { payload: company.id });
  const silaKYB = yield select(({ Company }) => Company.silaKYB);
  if (silaKYB.error) {
    return;
  }
  yield call(getCompanySilaKYBInterval, { payload: company.id });
  yield call(getPlaidLinkCompanyToken, {
    payload: {
      id: company.id,
      plaidToken: null,
      accounts: null
    }
  });
  yield call(KYBCallBack);
  yield call(getCompanyWallet, { payload: company.id });
}

function* updateCompanyInfo({ payload }) {
  // TODO: replace this chain of API calls with one request: all necessary request will be implemented on backend side
  const { companyData, silaData, KYBCallBack } = payload;

  const company = yield select((state) => state.Company.company);

  yield put(ACTIONS.clearCompany());
  yield call(updateCompany, { id: company.id, payload: companyData });
  const companyRequest = yield select((state) => state.Company.companyRequest);
  if (companyRequest.error) {
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_LABELS.NEW_COMPANY_ID, company.id);
  const silaCompanyCheck = yield select((state) => state.Company.silaCompany);
  // If user don't have a silaCompany and he tries to update company info
  if (silaCompanyCheck.companyInfo?.company !== company.id) {
    yield call(registerSilaCompany, {
      payload: { id: company.id, payload: silaData }
    });
    const silaCompany = yield select(({ Company }) => Company.silaCompany);
    if (silaCompany.error) {
      WIZARD_ACTIONS.setWalletAPIErrors(silaCompany.error);
      return;
    }
    yield call(postCompanyRequestKYB, { payload: company.id });
    const silaKYB = yield select(({ Company }) => Company.silaKYB);
    if (silaKYB.error) {
      return;
    }
    yield call(getCompanySilaKYBInterval, { payload: company.id });
    yield call(getPlaidLinkCompanyToken, {
      payload: {
        id: company.id,
        plaidToken: null,
        accounts: null
      }
    });
    yield call(KYBCallBack);
    return;
  }
  yield call(updateSilaCompany, {
    payload: { id: company.id, payload: silaData }
  });
  const silaCompany = yield select((state) => state.Company.silaCompany);
  if (silaCompany.error) {
    return;
  }
  yield call(postCompanyRequestKYB, { payload: company.id });
  const silaKYB = yield select(({ Company }) => Company.silaKYB);
  if (silaKYB.error) {
    return;
  }
  yield call(getCompanySilaKYBInterval, { payload: company.id });
  yield call(getPlaidLinkCompanyToken, {
    payload: {
      id: company.id,
      plaidToken: null,
      accounts: null
    }
  });
  yield call(KYBCallBack);
  yield call(getCompanyWallet, { payload: company.id });
}

function* fetchZealLoginUrl({ payload }) {
  try {
    const data = yield call(
      request,
      `/company/${payload.data}/zeal_login/`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.getCompanyZealLoginUrlSuccess(serializeKeys(data)));
  } catch (error) {
    payload.errCb();
    yield put(ACTIONS.getCompanyZealLoginUrlFail(error));
  }
}

export default function* companySaga() {
  yield takeLatest(CONSTANTS.FETCH_COMPANIES_REQUEST, fetchCompanies);
  yield takeLatest(CONSTANTS.FETCH_COMPANY_DETAIL_REQUEST, fetchCompanyDetail);
  yield takeLatest(CONSTANTS.REGISTER_SILA_COMPANY, registerSilaCompany);
  yield takeLatest(CONSTANTS.UPDATE_SILA_COMPANY, updateSilaCompany);
  yield takeLatest(CONSTANTS.GET_SILA_COMPANY, getCorporateSilaCompany);
  yield takeLatest(CONSTANTS.GET_COMPANY_WALLET, getCompanyWallet);
  yield takeLatest(CONSTANTS.CREATE_COMPANY_REQUEST, createCompany);
  yield takeLatest(CONSTANTS.UPDATE_COMPANY_REQUEST, updateCompany);
  yield takeLatest(
    CONSTANTS.FETCH_COMPANY_PROFILE_REQUEST,
    fetchCompanyProfile
  );
  yield takeEvery(
    CONSTANTS.UPDATE_COMPANY_PROFILE_REQUEST,
    updateCompanyProfile
  );
  yield takeEvery(
    CONSTANTS.ACCEPT_COMPANY_PERMISSION_BY_OWNER_REQUEST,
    acceptCompanyPermissionByOwner
  );
  yield takeEvery(
    CONSTANTS.DECLINE_COMPANY_PERMISSION_BY_OWNER_REQUEST,
    declineCompanyPermissionByOwner
  );
  yield takeEvery(CONSTANTS.FETCH_COMPANY_TYPE_REQUEST, fetchCompanyType);
  yield takeEvery(CONSTANTS.FETCH_BUSINESS_TYPE_REQUEST, fetchBusinessType);
  yield takeEvery(CONSTANTS.GET_COMPANY_SILA_VERIFICATION, getCompanySilaKYB);
  yield takeEvery(
    CONSTANTS.GET_COMPANY_SILA_VERIFICATION_INTERVAL,
    getCompanySilaKYBInterval
  );
  yield takeEvery(CONSTANTS.POST_COMPANY_REQUEST_KYB, postCompanyRequestKYB);
  yield takeLatest(
    CONSTANTS.UPLOAD_KYB_DOCUMENT_REQUEST,
    uploadKYBDocumentRequest
  );
  yield takeEvery(
    CONSTANTS.GET_PLAID_LINK_COMPANY_TOKEN_REQUEST,
    getPlaidLinkCompanyToken
  );
  yield takeEvery(
    CONSTANTS.GET_PLAID_COMPANY_ACCOUNT_REQUEST,
    getPlaidCompanyAccount
  );
  yield takeEvery(
    CONSTANTS.GET_COMPANY_PLAID_VERIFY_ACCOUNT_REQUEST,
    getCompanyPlaidVerifyAccounts
  );
  yield takeEvery(
    CONSTANTS.REMOVE_PLAID_COMPANY_ACCOUNT,
    removePlaidCompanyAccount
  );
  yield takeEvery(
    CONSTANTS.FETCHING_COMPANY_WALLET_BAL_REQUEST,
    fetchCompanyWalletBal
  );
  yield takeEvery(
    CONSTANTS.DELETE_COMPANY_ACCOUNT_REQUEST,
    deleteCompanyAccount
  );
  yield takeEvery(
    CONSTANTS.SET_COMPANY_PAYMENT_PLAN_REQUEST,
    setCompanyPaymentPlan
  );
  yield takeLatest(CONSTANTS.REGISTER_COMPANY, registerCompany);
  yield takeLatest(CONSTANTS.UPDATE_COMPANY, updateCompanyInfo);
  yield takeLatest(CONSTANTS.GET_ZEAL_LOGIN_URL_REQUEST, fetchZealLoginUrl);
}
